import React, { FC, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import { EpgEventDetailOverride, ParentalRating } from "../../../../../models/epg/Models";

interface ObjectListInputFieldProps<K extends keyof EpgEventDetailOverride> {
    label: string;
    items: ParentalRating[];
    isEditMode: boolean;
    fieldName: K;
    updateField: <K extends keyof EpgEventDetailOverride>(key: K, value: EpgEventDetailOverride[K]) => void;
}

const ObjectListInputField: FC<ObjectListInputFieldProps<keyof EpgEventDetailOverride>> = (
    { label, items, isEditMode, fieldName, updateField }: ObjectListInputFieldProps<keyof EpgEventDetailOverride>) => {
    const [ value, setValue ] = useState<ParentalRating | undefined>();

    const removeItem = (index: number) => {
        const newItems = [ ...items ];
        newItems.splice(index, 1);

        updateField(fieldName, newItems);
    };

    const addItem = () => {
        if (!value)
            return;
        const newItems = items ? [ ...items ] : [];
        newItems.push(value);
        updateField(fieldName, newItems);
        setValue(undefined);
    };

    return (
        <>
            <p className = {"text-slate-500 font-bold text-lg mb-2"}>{label}</p>
            <div className = {`w-full`}>
                <div className = {`flex items-end gap-2 mb-2 ${isEditMode ? "block" : "hidden"}`}>
                    <div className={`grid grid-cols-2 gap-y-8 gap-8 w-full`}>
                        <div className={`w-3/4`}>
                            <p className={`font-bold`}>Rating System</p>
                            <input className = {`p-2 w-full rounded-md bg-light ${isEditMode ? "text-gray-800" : ""}`} value = {value ? value.ratingSystem : ""}
                               type = {"text"} disabled = {!isEditMode} placeholder = {`Add an item`}
                               onChange = {(e) => {
                                   const updatedValue = e.target.value;
                                   setValue({
                                       ratingSystem: updatedValue,
                                       ratingLabel: value ? value.ratingLabel : "",
                                   });
                               }}
                            />
                        </div>
                        <div className = {`w-3/4`}>
                            <p className={`font-bold`}>Rating Label</p>
                            <input className = {`p-2 w-full rounded-md bg-light ${isEditMode ? "text-gray-800" : ""}`}
                                   value = {value ? value.ratingLabel : ""}
                                   type = {"text"} disabled = {!isEditMode} placeholder = {`Add an item`}
                                   onChange = {(e) => {
                                       const updatedValue = e.target.value;
                                       setValue({
                                           ratingLabel: updatedValue,
                                           ratingSystem: value ? value.ratingSystem : "",
                                       });
                                   }}
                            />
                        </div>
                    </div>
                    <button className = {`flex flex-row items-center gap-2 bg-blue-800 dark:bg-indigo-400 rounded-md shadow-xl p-2`} onClick={() => {
                        addItem();
                    }}
                    >
                        <FontAwesomeIcon icon = {faPlus} size = "1x" fixedWidth className={`text-white`} />
                    </button>
                </div>
                {
                    items && items.length > 0 ?
                        (<div className = {`flex flex-row gap-2 mt-2`}>
                            {
                                items.map((item, index) => (
                                    <div key = {`${item.ratingLabel}${item.ratingSystem}`}
                                         className = {`flex flex-row gap-1 items-center justify-center shadow-md border p-1 rounded-md bg-blue-800 dark:bg-indigo-400`}
                                    >
                                        <p className={`text-white`}>{`${item.ratingSystem} (${item.ratingLabel})`}</p>
                                        <button className = {`${isEditMode ? "block" : "hidden"}`}
                                            onClick = {() => {
                                                removeItem(index);
                                            }}
                                        >
                                            <FontAwesomeIcon icon = {faXmark} size = "sm" fixedWidth className={`text-white`} />
                                        </button>
                                    </div>
                                ))}
                         </div>) :
                        <div className = {`p-4 ${isEditMode ? "hidden" : "block"}`}>
                            <p className = {`font-bold text-slate-500`}>
                                {`No ${label} added yet`}
                            </p>
                        </div>
                }
            </div>
        </>);
};

export default ObjectListInputField;